import { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Grid,
  MenuItem,
  alpha,
  Divider,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import type { Location, LocationType } from '../../types';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';

interface UpdateLocationData {
  name: string;
  type: LocationType;
  code?: string;
  description?: string;
  address?: string;
  city?: string;
  wilaya?: string;
  zipCode?: string;
  country: string;
  phone?: string;
  email?: string;
  currency?: string;
  canSellToCustomers: boolean;
  canFulfillOnlineOrders: boolean;
  canReceiveTransfers: boolean;
  canSendTransfers: boolean;
  isActive: boolean;
}

const fetchLocation = async (id: string): Promise<Location> => {
  const response = await axiosInstance.get(`/locations/${id}`);
  return response.data;
};

export const LocationEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { mode } = useContext(ColorModeContext);
  const animations = useAnimationStyles();

  const [formData, setFormData] = useState<UpdateLocationData | null>(null);

  const { data: location, isLoading } = useQuery({
    queryKey: ['location', id],
    queryFn: () => fetchLocation(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (location) {
      setFormData({
        name: location.name,
        type: location.type,
        code: location.code || '',
        description: location.description || '',
        address: location.address || '',
        city: location.city || '',
        wilaya: location.wilaya || '',
        zipCode: location.zipCode || '',
        country: location.country,
        phone: location.phone || '',
        email: location.email || '',
        currency: location.currency || 'DZD',
        canSellToCustomers: location.canSellToCustomers,
        canFulfillOnlineOrders: location.canFulfillOnlineOrders,
        canReceiveTransfers: location.canReceiveTransfers,
        canSendTransfers: location.canSendTransfers,
        isActive: location.isActive,
      });
    }
  }, [location]);

  const updateMutation = useMutation({
    mutationFn: (data: UpdateLocationData) =>
      axiosInstance.patch(`/locations/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      queryClient.invalidateQueries({ queryKey: ['location', id] });
      showMessage('Location updated successfully', 'success');
      navigate(`/app/locations/${id}`);
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to update location', 'error');
    },
  });

  const handleChange = (field: keyof UpdateLocationData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!formData) return;
    const value = e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData) {
      updateMutation.mutate(formData);
    }
  };

  if (isLoading || !formData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, ...animations.fadeIn }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/app/locations/${id}`)}
          sx={{ color: palette.textSecondary }}
        >
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: typography.fontWeight.semibold }}>
          Edit Location
        </Typography>
      </Box>

      <Card sx={{ bgcolor: mode === 'dark' ? palette.surface : palette.light.surface }}>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Basic Info */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Name"
                  value={formData.name}
                  onChange={handleChange('name')}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  required
                  select
                  label="Type"
                  value={formData.type}
                  onChange={handleChange('type')}
                >
                  <MenuItem value="store">Store</MenuItem>
                  <MenuItem value="warehouse">Warehouse</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Code"
                  value={formData.code}
                  onChange={handleChange('code')}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Description"
                  value={formData.description}
                  onChange={handleChange('description')}
                />
              </Grid>

              {/* Address */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Address
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.address}
                  onChange={handleChange('address')}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.city}
                  onChange={handleChange('city')}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Wilaya"
                  value={formData.wilaya}
                  onChange={handleChange('wilaya')}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Zip Code"
                  value={formData.zipCode}
                  onChange={handleChange('zipCode')}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Country"
                  value={formData.country}
                  onChange={handleChange('country')}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Currency"
                  value={formData.currency}
                  onChange={handleChange('currency')}
                >
                  <MenuItem value="DZD">DZD - Algerian Dinar</MenuItem>
                  <MenuItem value="EUR">EUR - Euro</MenuItem>
                </TextField>
              </Grid>

              {/* Contact */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Contact
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.phone}
                  onChange={handleChange('phone')}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="email"
                  label="Email"
                  value={formData.email}
                  onChange={handleChange('email')}
                />
              </Grid>

              {/* Capabilities */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Capabilities
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.canSellToCustomers}
                      onChange={(e) =>
                        setFormData({ ...formData, canSellToCustomers: e.target.checked })
                      }
                    />
                  }
                  label="Can sell to customers"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.canFulfillOnlineOrders}
                      onChange={(e) =>
                        setFormData({ ...formData, canFulfillOnlineOrders: e.target.checked })
                      }
                    />
                  }
                  label="Can fulfill online orders"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.canReceiveTransfers}
                      onChange={(e) =>
                        setFormData({ ...formData, canReceiveTransfers: e.target.checked })
                      }
                    />
                  }
                  label="Can receive transfers"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.canSendTransfers}
                      onChange={(e) =>
                        setFormData({ ...formData, canSendTransfers: e.target.checked })
                      }
                    />
                  }
                  label="Can send transfers"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData({ ...formData, isActive: e.target.checked })
                      }
                    />
                  }
                  label="Active"
                />
              </Grid>

              {/* Actions */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                  <Button onClick={() => navigate(`/app/locations/${id}`)}>Cancel</Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={updateMutation.isPending}
                    sx={{
                      bgcolor: palette.primary,
                      '&:hover': { bgcolor: alpha(palette.primary, 0.9) },
                    }}
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LocationEdit;
